import { useEffect, useRef, useState } from "react";
import { cachedGet } from "../lib/sharedData";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, Check, CheckCheck, AlertTriangle, ClipboardCheck, Stethoscope, Activity, FileText, Target } from "lucide-react";

const KIND_ICON = {
  brief_assigned:    ClipboardCheck,
  submission_filed:  FileText,
  decision_made:     Check,
  fit_requested:     ClipboardCheck,
  fit_assessed:      ClipboardCheck,
  medical_requested: Stethoscope,
  medical_completed: Stethoscope,
  injury_reported:   Activity,
  slot_auto_upgraded: Target,
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrap = useRef(null);

  const load = (force = false) => cachedGet("/notifications", 60000, { force }).then((d) => {
    setItems(d.notifications || []);
    setUnread(d.unread || 0);
  }).catch(() => {});

  useEffect(() => { load(); const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const clickItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    setOpen(false);
    await load();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    await load();
  };

  return (
    <div ref={wrap} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors"
        data-testid="header-notifications-btn"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notif-unread-badge"
          >{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-96 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-display font-bold text-sm tracking-tight">Notifications {unread > 0 && <span className="text-emerald-700">· {unread} new</span>}</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid="notif-mark-all-read">
                <CheckCheck size={11} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto pm-scroll">
            {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="notif-empty">No notifications yet.</div>}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || AlertTriangle;
              const tone = n.kind === "slot_auto_upgraded" ? "text-red-600" : n.kind === "injury_reported" ? "text-amber-600" : "text-emerald-700";
              return (
                <button
                  key={n.id}
                  onClick={() => clickItem(n)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 border-b border-slate-50 last:border-b-0 ${!n.read ? "bg-emerald-50/30" : ""}`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className={`w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center ${tone} shrink-0`}>
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-bold text-[12px] text-slate-900 truncate">{n.title}</div>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                    </div>
                    {n.body && <div className="text-[11px] text-slate-600 truncate mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-0.5">{n.actor} · {timeAgo(n.created_at)} ago</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
